import { z } from 'zod';
import { CustomFieldValueSchema } from './custom-field-value.dto';
import { FilterSchema } from './idea-view.dto';
import { inferApiResultSchema, inferPaginatedApiResponse } from './infer-api';
import { IdeaPortalAssociationSchema } from './portal.dto';
import { DescriptionMaxLength } from './types.dto';

export const IdeaStatus = {
  New: 0,
  Discovery: 1,
  Review: 2,
  Development: 3,
  Parked: 4,
  Closed: 5,
  Cancelled: 6,
} as const;

export type IdeaStatusType = (typeof IdeaStatus)[keyof typeof IdeaStatus];

export const ideaTitleLimit = 300;

export const IdeaSchema = z.object({
  id: z.number(),
  accountId: z.string().max(50),
  ownerId: z.string().max(50),
  title: z.string().trim().min(1).max(ideaTitleLimit),
  description: z.string().max(DescriptionMaxLength).nullish(),
  createdAt: z.date().nullish(),
  updatedAt: z.date().nullish(),
  deletedAt: z.date().nullish(),
  archivedAt: z.date().nullish(),
  status: z.number(),
  fields: z.array(CustomFieldValueSchema),
  isPublished: z.boolean(),
});
export type IdeaDto = z.infer<typeof IdeaSchema>;

export const IdeaNoIdSchema = IdeaSchema.omit({
  id: true,
});
export type IdeaNoIdSchema = z.infer<typeof IdeaNoIdSchema>;

export const IdeaSummarySchema = IdeaSchema.extend({
  voteCount: z.number(),
  myVoteCount: z.number(),
  commentCount: z.number(),
  attachmentCount: z.number(),
  productAreas: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
      archivedAt: z.coerce.date().nullish(),
    }),
  ),
  portals: z.array(IdeaPortalAssociationSchema).optional(),
  rank: z.string().nullish(),
});

export const MatrixIdeaSummarySchema = IdeaSchema.omit({
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
  isPublished: true,
  status: true,
});

export type IdeaSummaryDto = z.infer<typeof IdeaSummarySchema>;
export type MatrixIdeaSummaryDto = z.infer<typeof MatrixIdeaSummarySchema>;

export const GetIdeaResponseSchema = inferApiResultSchema(IdeaSummarySchema);
export type GetIdeaResponseDto = z.infer<typeof GetIdeaResponseSchema>;

/**
 * What a portal contact may see of an idea. Everything else on the row is internal - ownership,
 * demand linkage, workflow timing, lifecycle, publication state, custom-field values and the
 * generated search vector, which reconstructs title, description and searchable field values.
 *
 * Deliberately not derived from IdeaSummarySchema: that one describes a write, so it carries
 * length limits and a trim() transform that mean nothing on the way out, and it widens nullable
 * columns to optional. This describes exactly what the portal endpoints emit.
 */
export const PublicIdeaSummarySchema = z.object({
  id: z.number(),
  title: z.string(),
  description: z.string().nullable(),
  status: z.number(),
  createdAt: z.date().nullable(),
  voteCount: z.number(),
  myVoteCount: z.number(),
  commentCount: z.number(),
  productAreas: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
      archivedAt: z.coerce.date().nullish(),
    }),
  ),
});
export type PublicIdeaSummaryDto = z.infer<typeof PublicIdeaSummarySchema>;

export const GetPublicIdeaResponseSchema = inferApiResultSchema(PublicIdeaSummarySchema);
export type GetPublicIdeaResponseDto = z.infer<typeof GetPublicIdeaResponseSchema>;

export const GetPublicIdeasResponseSchema = inferPaginatedApiResponse(
  z.array(PublicIdeaSummarySchema),
);
export type GetPublicIdeasResponseDto = z.infer<typeof GetPublicIdeasResponseSchema>;

export const CreateIdeaSchema = IdeaSchema.omit({
  id: true,
  accountId: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
  archivedAt: true,
  ownerId: true,
  isPublished: true,
}).extend({
  productAreaIds: z.array(z.number()).nonempty(),
  isPublished: z.boolean().default(false).optional(),
});
export type CreateIdeaDto = z.infer<typeof CreateIdeaSchema>;

export const CreateIdeaFromFeedbackSchema = z.object({
  ideaName: z.string(),
  feedbackId: z.number(),
});
export type CreateIdeaFromFeedbackDto = z.infer<typeof CreateIdeaFromFeedbackSchema>;

export const CreateIdeaResponseSchema = inferApiResultSchema(IdeaSchema);
export type CreateIdeaReponseDto = z.infer<typeof CreateIdeaResponseSchema>;

export const UpdateIdeaSchema = IdeaSchema.omit({
  id: true,
  accountId: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
  archivedAt: true,
  ownerId: true,
})
  .extend({ productAreaIds: z.array(z.number()) })
  .partial();
export type UpdateIdeaDto = z.infer<typeof UpdateIdeaSchema>;

export const UpdateIdeaResponseSchema = inferApiResultSchema(IdeaSummarySchema);
export const UpdateIdeaSummaryResponseSchema = inferApiResultSchema(IdeaSummarySchema);
export type UpdateIdeaResponseDto = z.infer<typeof UpdateIdeaResponseSchema>;
export type UpdateIdeaSummaryResponseDto = z.infer<typeof UpdateIdeaSummaryResponseSchema>;

export const BulkIdeaDtoSchema = z.object({
  ids: z.array(z.number().min(1)).nonempty(),
});
export const BulkRowsAffectedSchema = z.object({
  rowsAffected: z.number(),
});

export const GetIdeasResponseSchema = inferPaginatedApiResponse(z.array(IdeaSummarySchema));
export type GetIdeasResponseDto = z.infer<typeof GetIdeasResponseSchema>;

export const GetMatrixIdeasResponseSchema = inferApiResultSchema(z.array(MatrixIdeaSummarySchema));
export type GetMatrixIdeasResponseDto = z.infer<typeof GetMatrixIdeasResponseSchema>;

export const LinkedRoadmapItemSchema = z.object({
  ideaId: z.number(),
  roadmapItemId: z.string().length(24),
  loopId: z.string().optional(),
});
export type LinkedRoadmapItemDto = z.infer<typeof LinkedRoadmapItemSchema>;

export const CreateLinkedRoadmapItemSchema = LinkedRoadmapItemSchema.omit({ ideaId: true });
export type CreateLinkedRoadmapItemDto = z.infer<typeof CreateLinkedRoadmapItemSchema>;

export const DemandStatsSchema = z.object({
  demandIdeaCount: z.number().int().nonnegative(),
  relatedFeedbackCount: z.number().int().nonnegative(),
  approvedDemandCount: z.number().int().nonnegative(),
  activeWorkCount: z.number().int().nonnegative(),
  ideasAwaitingReviewCount: z.number().int().nonnegative(),
  endToEndConversionPct: z.number().nonnegative(),
});
export type DemandStatsDto = z.infer<typeof DemandStatsSchema>;

export const LinkedRoadmapFilterSchema = z.object({
  roadmapItemIds: z.array(z.string().length(24)).optional(),
});
export type LinkedRoadmapFilterDto = z.infer<typeof LinkedRoadmapFilterSchema>;

export const LinkedIdeaSummarySchema = IdeaSummarySchema.extend({
  customersCount: z.number(),
  feedbacksCount: z.number(),
  linkedRoadmapItems: z.array(LinkedRoadmapItemSchema),
});
export type LinkedIdeaSummaryDto = z.infer<typeof LinkedIdeaSummarySchema>;

export const RestrictedIdeaSchema = z.object({
  id: z.number(),
  accountId: z.string().max(50),
  ownerId: z.string().max(50),
  createdAt: z.date().nullish(),
  updatedAt: z.date().nullish(),
});
export type RestrictedIdeaDto = z.infer<typeof RestrictedIdeaSchema>;

export const GetLinkedRoadmapItemsResponseSchema = inferApiResultSchema(
  z.array(LinkedIdeaSummarySchema),
);
export type GetLinkedRoadmapItemsResponseDto = z.infer<typeof GetLinkedRoadmapItemsResponseSchema>;

export const GetRestrictedLinkedIdeasResponseSchema = inferApiResultSchema(
  z.array(RestrictedIdeaSchema),
);
export type GetRestrictedLinkedIdeasResponseDto = z.infer<
  typeof GetRestrictedLinkedIdeasResponseSchema
>;

export const IdeaStatusSummarySchema = z.object({
  new: z.number(),
  discovery: z.number(),
  review: z.number(),
  development: z.number(),
  parked: z.number(),
  closed: z.number(),
  cancelled: z.number(),
  archived: z.number(),
  total: z.number(),
});
export type IdeaStatusSummaryDto = z.infer<typeof IdeaStatusSummarySchema>;

export const IdeaRankSchema = z.object({
  accountId: z.string().max(50),
  ideaViewId: z.number(),
  ideaId: z.number(),
  rank: z.string().min(1),
});
export type IdeaRankDto = z.infer<typeof IdeaRankSchema>;

export const IdeaSortSchema = z.object({
  column: z.string(),
  order: z.enum(['asc', 'desc']).default('desc'),
});
export type IdeaSortDto = z.infer<typeof IdeaSortSchema>;

export const BatchAssignRanksSchema = z.object({
  ideaViewId: z.number(),
  filter: FilterSchema,
  order: IdeaSortSchema.optional(),
  overwrite: z.boolean().optional(),
});
export type BatchAssignRanksDto = z.infer<typeof BatchAssignRanksSchema>;

export const UpdateIdeaRankSchema = z.object({
  ideaViewId: z.number(),
  previousIdeaId: z.number().optional(),
  ideaId: z.number(),
  nextIdeaId: z.number().optional(),
});
export type UpdateIdeaRankDto = z.infer<typeof UpdateIdeaRankSchema>;

export const UpdateIdeaRankResponseSchema = z.object({
  rank: z.string(),
});
export type UpdateIdeaRankResponseDto = z.infer<typeof UpdateIdeaRankResponseSchema>;

export const ReviewReason = {
  Stale: 'stale',
  NoVotes: 'no_votes',
  ReadyToArchive: 'ready_to_archive',
} as const;

export type ReviewReasonType = (typeof ReviewReason)[keyof typeof ReviewReason];

export const ReviewReasonSchema = z.nativeEnum(ReviewReason);

export const IdeaRequiringReviewSchema = z.object({
  id: z.number(),
  title: z.string(),
  voteCount: z.number(),
  reviewReason: ReviewReasonSchema,
});
export type IdeaRequiringReviewDto = z.infer<typeof IdeaRequiringReviewSchema>;

export const GetIdeasRequiringReviewResponseSchema = z.object({
  data: z.array(IdeaRequiringReviewSchema),
  totalCount: z.number(),
});
export type GetIdeasRequiringReviewResponseDto = z.infer<
  typeof GetIdeasRequiringReviewResponseSchema
>;
